"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import {
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  CalendarDays,
  Check,
  ExternalLink,
  Heart,
  MapPin,
  Play,
  Radio,
} from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatDate } from "@/lib/format";
import { enroll, likeModule, saveModule } from "@/lib/lecturehub/module-actions";
import type { EventDetailEntity } from "@/lib/lecturehub/types";

type Props = {
  lang: Lang;
  moduleId: number;
  event: EventDetailEntity | null | undefined;
  // Events are a free library — no pricing/paywall (verified against live
  // data: every event module is free with a null price). No `price`/`canView`
  // prop: access is gated by free enrolment, not payment.
  free: boolean;
  enrolled: boolean;
  isPast: boolean;
  isLive: boolean;
  isAuthed: boolean;
  /** Portal runs in subscription mode (`portal.paymentModel`). Events carry no
   *  price of their own, but a MODULE_NOT_ENROLLABLE still has to land the user
   *  somewhere real — plans in subscription mode, per-item checkout otherwise. */
  isSubscription: boolean;
  initialLiked: boolean;
  initialSaved: boolean;
  /** Link to the first recording, set only for enrolled users on the in-app
   *  /library surface. When present the enrolled CTA becomes "Watch recordings". */
  watchHref?: string | null;
  /** Show the "Save" action alongside "Favorites". Default true; the event
   *  detail passes false so it shows Favorites only (matches other detail pages). */
  showSave?: boolean;
};

export function EventStartCard({
  lang,
  moduleId,
  event: ev,
  free,
  enrolled: initialEnrolled,
  isPast,
  isLive,
  isAuthed,
  isSubscription,
  initialLiked,
  initialSaved,
  watchHref,
  showSave = true,
}: Props) {
  const t = useDict();
  const router = useRouter();
  const [enrolled, setEnrolled] = useState(initialEnrolled);
  const [liked, setLiked] = useState(initialLiked);
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function loginUrl(): string {
    return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
  }

  function toggleLike() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !liked;
    setLiked(next);
    startTransition(async () => {
      const r = await likeModule(moduleId, next);
      if (!r.ok) {
        setLiked(!next);
        setError(r.error ?? null);
      }
    });
  }

  function toggleSave() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const r = await saveModule(moduleId, next);
      if (!r.ok) {
        setSaved(!next);
        setError(r.error ?? null);
      }
    });
  }

  function doRegister() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    setError(null);
    startTransition(async () => {
      const r = await enroll(moduleId);
      if (r.ok) {
        // Flip the button optimistically, then refresh. enroll() busts the
        // module cache (updateTag), so the refresh re-runs the detail page
        // server-side: enrolment is now recorded, so EventDetailClient computes
        // a real `watchHref` and this CTA turns into "Watch recordings". Without
        // the refresh the button flips but the watch link never arrives.
        setEnrolled(true);
        router.refresh();
      } else if (r.code === "MODULE_NOT_ENROLLABLE") {
        // Subscription mode → plans; single mode → per-item checkout.
        // (Same branch as the other start cards. /profil/narocnine does exist on a
        // single-payment portal, but with no subscriptionPlans it renders the
        // FreePortalNotice explainer — nothing purchasable — so checkout is the
        // only destination that can actually unlock the module.)
        router.push(langPath(lang, isSubscription ? "/profil/narocnine" : `/checkout/${moduleId}`));
      } else setError(r.error ?? null);
    });
  }

  const location =
    ev?.city && ev?.country ? `${ev.city}, ${ev.country}` : (ev?.city ?? ev?.country ?? null);

  // Editorial gold pill — matches dashboard / CourseStartCard / MediaSidebar.
  const ctaClass =
    "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent text-accent-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100";
  const ctaStyle = {
    boxShadow:
      "0 2px 4px rgb(0 0 0 / 0.10), 0 8px 18px -6px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 45%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 70%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
  };

  // Events are an evergreen library — a past date is informational only (it's
  // still shown in the detail rows below), never a reason to block access.
  // There is no "this event has ended" terminal state; the CTA follows the
  // same enrolment/access flow as every other module. `isPast` now only
  // suppresses the live badge.
  let cta: React.ReactNode;
  if (enrolled && watchHref) {
    cta = (
      <Link href={watchHref} className={ctaClass} style={ctaStyle}>
        <Play className="size-4" aria-hidden />
        {t.detail.watchRecordings}
        <ArrowRight className="size-3.5" aria-hidden />
      </Link>
    );
  } else if (enrolled) {
    cta = (
      <div className="inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent/15 text-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px]">
        <Check className="size-4 text-accent" aria-hidden />
        {t.detail.registeredBadge}
      </div>
    );
  } else {
    cta = (
      <button
        type="button"
        onClick={doRegister}
        disabled={pending}
        className={`${ctaClass} disabled:opacity-60 disabled:pointer-events-none`}
        style={ctaStyle}
      >
        <CalendarDays className="size-4" aria-hidden />
        {pending ? "…" : t.detail.registerCta}
        <ArrowRight className="size-3.5" aria-hidden />
      </button>
    );
  }

  return (
    // Sticky lives on the parent column wrapper (EventDetailClient) — see
    // CourseStartCard / MediaSidebar for the same pattern.
    <aside className="event-start">
      <div className="bg-card text-card-foreground rounded-card border border-border/60 p-6 md:p-7 overflow-hidden">
        {/* Price + live badge — prominent, standardised with CourseStartCard. */}
        <div className="flex items-start justify-between gap-3">
          {free ? (
            <p className="font-display text-[22px] font-extrabold text-accent">{t.detail.free}</p>
          ) : (
            <span aria-hidden />
          )}
          {isLive && !isPast ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-destructive">
              <Radio className="size-3 animate-pulse" aria-hidden />
              {t.detail.live}
            </span>
          ) : null}
        </div>

        {/* CTA + Like — Like as a compact circle to the right of the primary
            CTA, standardised with CourseStartCard / MediaSidebar. */}
        <div className="mt-4 flex items-center gap-2">
          <div className="min-w-0 flex-1">{cta}</div>
          <button
            type="button"
            onClick={toggleLike}
            aria-pressed={liked}
            aria-label={liked ? t.detail.liked : t.detail.like}
            title={liked ? t.detail.liked : t.detail.like}
            className={cn(
              "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
              liked
                ? "border-accent/30 bg-accent/15 text-accent"
                : "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground",
            )}
          >
            <Heart className={cn("size-5", liked ? "fill-current" : "")} aria-hidden />
          </button>
          {showSave ? (
            <button
              type="button"
              onClick={toggleSave}
              aria-pressed={saved}
              aria-label={saved ? t.detail.saved : t.detail.save}
              title={saved ? t.detail.saved : t.detail.save}
              className={cn(
                "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
                saved
                  ? "border-accent/30 bg-accent/15 text-accent"
                  : "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground",
              )}
            >
              {saved ? (
                <BookmarkCheck className="size-5" aria-hidden />
              ) : (
                <Bookmark className="size-5" aria-hidden />
              )}
            </button>
          ) : null}
        </div>

        <dl className="mt-6 grid gap-2.5 text-[12.5px]">
          {ev?.dateFrom ? (
            <Row
              Icon={CalendarDays}
              label={t.detail.starts}
              value={formatDate(ev.dateFrom, lang)}
            />
          ) : null}
          {ev?.dateTo && ev.dateTo !== ev.dateFrom ? (
            <Row Icon={CalendarDays} label={t.detail.ends} value={formatDate(ev.dateTo, lang)} />
          ) : null}
          {location ? <Row Icon={MapPin} label={t.detail.location} value={location} /> : null}
        </dl>

        {ev?.url ? (
          <a
            href={ev.url}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 text-[11.5px] font-display font-bold uppercase tracking-[0.12em] text-accent hover:brightness-110 transition-all"
          >
            <ExternalLink className="size-3.5" aria-hidden />
            {t.detail.eventWebsite}
          </a>
        ) : null}

        {error ? <p className="text-xs text-destructive mt-3">{error}</p> : null}
      </div>
    </aside>
  );
}

function Row({
  Icon,
  label,
  value,
}: {
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      <dt className="text-muted-foreground flex-1">{label}</dt>
      <dd className="font-bold text-foreground text-right">{value}</dd>
    </div>
  );
}
